import { useEffect, useState } from "react";
import { InviteVoiceConsent } from "@/components/story/InviteVoiceConsent";
import { useParams } from "react-router-dom";
import { Heart, ShieldCheck, Gift } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { AudioPlayer } from "@/components/AudioPlayer";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { api, apiError } from "@/lib/api";
import { SignedImg } from "@/lib/media";
import { toast } from "sonner";

const PROMPTS = [
  "What does this person mean to you?",
  "What's one memory of them you'll never forget?",
  "What have they taught you?",
  "What makes you smile when you think about them?",
  "What would you like them to know?",
];

const fromName = (i) => i.created_by ? `${i.created_by}${i.created_by_label ? ` (${i.created_by_label})` : ""}` : "your family";

export default function Reply() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [consent, setConsent] = useState(true);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then((r) => { setInfo(r.data); setSent(r.data.already_replied); })
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const send = async (blob, duration, name) => {
    if (!consent) return toast.error("Please tick the box to share your recording with your family.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, name || "reply.webm");
      fd.append("duration", duration);
      fd.append("consent", "true");
      toast.info("Sending your memory…");
      await api.post(`/invite/${token}/reply`, fd);
      setSent(true);
      toast.success("Your memory has been sent");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (error) return (
    <div className="min-h-screen legacy-bg flex items-center justify-center px-6">
      <div className="glass-card p-10 text-center max-w-md">
        <BrandMark size={44} className="mx-auto mb-5" />
        <h1 className="font-serif text-3xl text-[#EDE6F7] mb-3">Link not found</h1>
        <p className="text-[#B9B3C8]">{error}</p>
      </div>
    </div>
  );
  if (!info) return <div className="min-h-screen legacy-bg" />;

  return (
    <div className="min-h-screen legacy-bg">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><BrandMark size={40} /></div>
          <p className="eyebrow mb-3">{info.family_name}</p>
          {info.photo && (
            <SignedImg path={info.photo} invite={token} alt={info.member_name}
              className="mx-auto h-24 w-24 rounded-full object-cover border border-[#33264A] mb-4" />
          )}
          <h1 className="font-serif text-4xl text-[#EDE6F7]">Hello, {info.member_name}</h1>
          <p className="text-lg text-[#B9B3C8] mt-3" data-testid="invite-intro">
            {info.sender_name ? <span className="text-[#EDE6F7]" data-testid="invite-sender-name">{info.sender_name}</span> : "Someone in your family"} has recorded a personal message for you. They'd love you to record one in return — between 1 and 4 minutes.
          </p>
        </div>

        {info.memory && (
          <section className="glass-card p-6 mb-8">
            <p className="eyebrow mb-3" data-testid="invite-memory-label">{info.sender_name ? `A memory from ${info.sender_name}` : "A memory left for you"}</p>
            <AudioPlayer path={info.memory.audio_path} invite={token} label="Listen to their message" testId="invite-memory-player" />
            {info.memory.transcript && (
              <p className="mt-4 text-lg text-[#EDE6F7] font-cormorant leading-relaxed">{info.memory.transcript}</p>
            )}
          </section>
        )}

        {sent ? (
          <section className="glass-card p-10 text-center" data-testid="reply-sent">
            <Gift className="h-14 w-14 text-[#B57BFF] mx-auto mb-4" />
            <h2 className="font-serif text-3xl text-[#EDE6F7] mb-2">Thank you</h2>
            <p className="text-lg text-[#B9B3C8]">
              Your voice will become part of a surprise for {fromName(info)} — kept safely until the family's story is complete.
            </p>
          </section>
        ) : (
          <section className="glass-card p-6 sm:p-8" data-testid="reply-recorder-section">
            <h2 className="font-serif text-3xl text-[#EDE6F7] mb-2">What is your first memory of them?</h2>
            <p className="text-lg text-[#B9B3C8] mb-4">A place, a smell, a saying, something funny, something small that became important. Anything you like.</p>
            <button onClick={() => setShowMore(!showMore)} className="gold-text underline mb-3" data-testid="reply-prompts-toggle">
              A little inspiration
            </button>
            {showMore && (
              <ul className="list-disc pl-5 space-y-1 text-[#B9B3C8] mb-5">
                {PROMPTS.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}
            <VoiceRecorder onSave={send} saving={saving} saveLabel="Send my memory" />
            <div className="mt-6 rounded-xl bg-[#1A1222] border border-[#33264A] p-4">
              <p className="text-sm text-[#B9B3C8] mb-3">
                How your recording is used: it's shared privately with {fromName(info)} and becomes
                part of their family story. It isn't public and can't be found by searching.{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="gold-text underline">Read the privacy note</a>.
              </p>
              <label className="flex items-start gap-3 text-[#EDE6F7] cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  data-testid="reply-consent" className="mt-1.5 h-5 w-5 accent-[#8B2CFF]" />
                <span>I'm happy for this recording to be shared privately with my family as part of their story.</span>
              </label>
            </div>
          </section>
        )}

        <InviteVoiceConsent token={token} />

        <p className="flex items-center justify-center gap-2 text-[#5E5670] text-sm mt-8">
          <ShieldCheck className="h-4 w-4" /> Private &amp; family-only. Never shared publicly.
        </p>
      </div>
    </div>
  );
}
